// Firm detection utilities based on hostname

import {
	DEFAULT_ENTRIES_DISPLAY,
	DEFAULT_WORK_WEEK,
	type EntriesDisplayConfig,
	HARDCODED_PARAMS,
	type WorkWeek,
} from "@/shared/config/hardcodedParams";

export type FirmDomain =
	| "pregodonnell"
	| "bpmlaw"
	| "tysonmendes"
	| "taylorday"
	| "namanhowell"
	| "gouldratner"
	| "test"
	| "hourglass"
	| "unknown";

/**
 * Detects the current firm based on window.location.hostname
 */
export function getFirmDomain(): FirmDomain {
	const hostname = window.location.hostname;

	if (hostname.includes("pregodonnell")) return "pregodonnell";
	if (hostname.includes("bpmlaw")) return "bpmlaw";
	if (hostname.includes("tysonmendes")) return "tysonmendes";
	if (hostname.includes("taylorday")) return "taylorday";
	if (hostname.includes("namanhowell")) return "namanhowell";
	if (hostname.includes("gouldratner")) return "gouldratner";
	if (hostname.includes("test")) return "test";

	// Local dev: allow env override since localhost can't be detected by hostname
	if (hostname.includes("localhost")) {
		const validDomains: FirmDomain[] = [
			"pregodonnell",
			"bpmlaw",
			"tysonmendes",
			"taylorday",
			"namanhowell",
			"gouldratner",
			"test",
			"hourglass",
		];
		const envOverride = import.meta.env.VITE_FIRM_DOMAIN;
		if (envOverride && validDomains.includes(envOverride as FirmDomain))
			return envOverride as FirmDomain;
		return "hourglass";
	}

	if (hostname.includes("hourglass")) return "hourglass";

	return "unknown";
}

/**
 * Returns true if the current firm is Preg O'Donnell
 */
export function isPregODonnell(): boolean {
	return getFirmDomain() === "pregodonnell";
}

/**
 * Returns true if the current firm is Taylor Day
 */
export function isTaylorDay(): boolean {
	return getFirmDomain() === "taylorday";
}

/**
 * Returns true if the current firm is Naman Howell
 */
export function isNamanHowell(): boolean {
	return getFirmDomain() === "namanhowell";
}

/**
 * Returns true if the current firm is Tyson Mendes
 */
export function isTysonMendes(): boolean {
	return getFirmDomain() === "tysonmendes";
}

/**
 * Returns true if the current firm is Gould Ratner
 */
export function isGouldratner(): boolean {
	return getFirmDomain() === "gouldratner";
}

/**
 * First day of the week for calendar views.
 * Tyson Mendes = 1 (Monday); all other firms = 0 (Sunday).
 */
export function getWeekStartsOn(): 0 | 1 {
	return HARDCODED_PARAMS[getFirmDomain()].weekStartsOn;
}

export function getFirmDisplayName(): string {
	return HARDCODED_PARAMS[getFirmDomain()].displayName;
}

export function getWorkWeek(): WorkWeek {
	return HARDCODED_PARAMS[getFirmDomain()].workWeek ?? DEFAULT_WORK_WEEK;
}

/**
 * Billing-related display defaults for the current firm's Entries view.
 * Falls back to `DEFAULT_ENTRIES_DISPLAY` (everything shown) when the firm
 * doesn't override it.
 */
export function getEntriesDisplay(): EntriesDisplayConfig {
	return (
		HARDCODED_PARAMS[getFirmDomain()].entriesDisplay ?? DEFAULT_ENTRIES_DISPLAY
	);
}

/** Returns the work-week range containing `reference` per the current firm. */
export function workWeekRange(reference: Date): { from: Date; to: Date } {
	const { startDay, length } = getWorkWeek();
	const dow = reference.getDay();
	const offset = (dow - startDay + 7) % 7;
	const from = new Date(
		reference.getFullYear(),
		reference.getMonth(),
		reference.getDate() - offset,
	);
	const to = new Date(from);
	to.setDate(from.getDate() + (length - 1));
	return { from, to };
}
